import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ReactNode, useEffect, useState } from 'react';

import { Alert, Button } from '@boss/ui';

import { useLogin } from '../../hooks';

type Props = {
  behavior: 'showLogin' | 'showAlways' | 'showNothingWhenLoggedOut';
  children: ReactNode;
};

const FormWrapper = ({ behavior, children }: Props) => {
  const { t } = useTranslation(['forms', 'common']);
  const { handleLogin, isLoggedIn, handleRegister, profileStatus } = useLogin();

  const { asPath } = useRouter();

  const [showLogin, setShowLogin] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const isGated = ['showLogin', 'showNothingWhenLoggedOut'].includes(behavior);

    // [45260] No-flash idle-wait: for gated behaviors defer the decision until the profile is
    // resolved — a logged-in klant must not see the Alert flash while the profile settles and
    // the form must never paint before the login check. Mirrors usePermissionGuard's status wait.
    if (isGated && profileStatus !== 'idle') {
      return;
    }

    if (isGated && !isLoggedIn) {
      setShowLogin(true);
    } else {
      setShowForm(true);
    }
  }, [behavior, isLoggedIn, profileStatus]);

  if (showLogin) {
    return (
      <Alert className="mx-auto w-full gap-4 text-left" type="info">
        <p className="inline">
          {t('unavailable.title', { ns: 'forms' })}. &nbsp;
          <Button
            className="inline p-0"
            label={t('unavailable.register')}
            onClick={() => handleRegister(asPath)}
            type="link"
          />
          {t('or', { ns: 'common' })}
          <Button
            className="inline p-0 md:text-[1.25rem]"
            label={t('unavailable.login')}
            labelClassName="lowercase"
            onClick={() => handleLogin(asPath)}
            type="link"
          />
        </p>
      </Alert>
    );
  }
  if (!showForm) {
    return null;
  }
  return <>{children}</>;
};

export default FormWrapper;
